/**
 * Account auth: sign-out + the mobile-OAuth redirect-return plumbing.
 *
 * NOTE (OTP migration, 2026-07-22): the OTP era removed Google/Apple/email
 * entry points, leaving this the redirect-return consumer
 * (`consumeAuthRedirectResult` / `hasPendingAuthRedirect`) that `AuthGateContext`
 * + `useAuthInFlux` mount on every load, plus the anon→permanent merge helpers
 * the OTP sign-in path still depends on.
 *
 * NOTE (v4, 2026-09-02): v4's normal door (`v4Auth.ts`, behind the ?v4 flag)
 * reintroduced Google/Apple redirect sign-in on mobile — it WRITES the redirect
 * marker again and resumes here via `consumeAuthRedirectResult`. Its
 * `credential-already-in-use` branch (the guest linked into an OAuth account
 * that ALREADY belongs to a real user) now signs into that existing account
 * WITHOUT merging — the guest board is left behind, matching the v4 desktop
 * popup path (`v4Auth.signInWithOAuthProvider`) and the settled One Rule that
 * boards attach ONLY at brand-new-account creation
 * (feedback_no_merge_into_existing_account). The new-account path
 * (`linkWithRedirect` succeeded, same uid) is the untouched success return.
 *
 * The remaining merge helpers (`captureMergeIntent` / `tryMerge` /
 * `signInWithCustomTokenAndMerge`) are now used ONLY by the OTP sign-in path
 * (`useOtpSignIn`); the OAuth-redirect return no longer merges. The merge call
 * there is best-effort — failures are logged but do NOT block sign-in.
 */

import {
  GoogleAuthProvider,
  OAuthProvider,
  type Auth,
  type AuthCredential,
  type User,
  type UserCredential,
  getAdditionalUserInfo,
  getRedirectResult,
  signInWithCredential,
  signInWithCustomToken,
  signOut,
} from 'firebase/auth';

import { auth as defaultAuth } from '../../firebaseConfig';
import { mergeGiftFlow, mintMergeToken } from '../callables';
import { writeHasBoardsHint } from '../lib/hasBoardsHint';
import type { MergeStatus } from './MergeStateContext';

/**
 * Optional callback the sign-in flow uses to drive `MergeStateContext` so
 * downstream listeners can pause re-subscribing while the anon → permanent
 * fan-out is in flight (sheet bug #58). Called with 'merging' before auth
 * flips, then 'merged' on success or 'failed' on merge error. When the
 * sign-in itself fails before any merge attempt, fires 'idle' so the
 * status doesn't stay stuck.
 */
export type OnMergeStatus = (status: MergeStatus) => void;

export function isMobileUserAgent(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

/**
 * Redirect marker — persisted to sessionStorage immediately before any
 * `signInWithRedirect`/`linkWithRedirect` call, so the post-redirect mount can
 * tell "we initiated a redirect" apart from "this is just a cold load." If
 * the marker is present but the credential never arrives (storage
 * partitioning, ITP, bfcache, or just a user who hit Back), the gate fires
 * an `auth_redirect_lost` telemetry event and surfaces a retry affordance
 * instead of silently leaving the user signed out (Bug 2 detection).
 */
const REDIRECT_MARKER_KEY = 'thea:authRedirectStarted';

/**
 * Which Firebase call we handed to the provider. It is NOT recoverable from the
 * return leg, and it is the only honest way to name created-vs-existing there:
 *
 *   'link'   — `linkWithRedirect(anon, provider)`. Succeeding means the provider
 *              identity was unknown to Firebase, so the anon uid was upgraded in
 *              place: a BRAND-NEW account. Identity Toolkit mints no new account
 *              record for a link, so `isNewUser` comes back false regardless —
 *              it cannot be read as "existing" here (THE-712).
 *   'signin' — `signInWithRedirect`. A new identity DOES mint a new record, so
 *              `isNewUser` is trustworthy on this leg.
 *
 * `null` = a marker written before this field existed (a redirect in flight
 * across the deploy boundary), or a redirect we did not initiate.
 */
export type RedirectOperation = 'link' | 'signin';

export interface RedirectMarker {
  provider: 'apple' | 'google';
  operation: RedirectOperation | null;
  ts: number;
}

/**
 * Read the link-vs-signin operation off the redirect result ITSELF, via
 * Firebase's own `operationType` (part of every `UserCredential`: 'link' |
 * 'signIn' | 'reauthenticate') — never the sessionStorage marker.
 *
 * THE-1375 Shepherd QA finding 4: the marker alone is not reliable — it can
 * be lost (in-app browsers, storage wipes, a quota error swallowed by
 * `writeRedirectMarker`) or stale (no age check in `readAndClearRedirectMarker`,
 * so a leftover marker from an earlier aborted attempt can pair with an
 * unrelated later redirect). `operationType` can't be lost that way: it rides
 * back on the credential itself, from the SAME call `getRedirectResult`/
 * `signInWithCredential` just made. Preferring it makes the marker a pure
 * fallback — only consulted when the result carries no `operationType` at all
 * (a marker written before this function existed to read it, in principle;
 * never observed from a real Firebase SDK response).
 */
function operationFromResult(result: UserCredential | null): RedirectOperation | null {
  if (result?.operationType === 'link') return 'link';
  if (result?.operationType === 'signIn') return 'signin';
  return null;
}

function parseRedirectMarker(raw: string | null): RedirectMarker | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<RedirectMarker>;
    if (
      (parsed.provider === 'apple' || parsed.provider === 'google') &&
      typeof parsed.ts === 'number'
    ) {
      const operation =
        parsed.operation === 'link' || parsed.operation === 'signin' ? parsed.operation : null;
      return { provider: parsed.provider, operation, ts: parsed.ts };
    }
    return null;
  } catch {
    return null;
  }
}

function readAndClearRedirectMarker(): RedirectMarker | null {
  try {
    const raw = sessionStorage.getItem(REDIRECT_MARKER_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(REDIRECT_MARKER_KEY);
    return parseRedirectMarker(raw);
  } catch {
    return null;
  }
}

/**
 * How long a marker counts as "a sign-in is probably still mid-flight."
 * The provider round-trip is seconds on the happy path but can stretch to
 * minutes when the user types a password or passes 2FA. Past this age the
 * marker is treated as abandoned (user bailed at the provider and came
 * back much later) so it can't hold pending UI on an unrelated visit.
 */
const REDIRECT_PENDING_MAX_AGE_MS = 10 * 60 * 1000;

/**
 * Module-latched "redirect resolution in flight" state. `null` = not yet
 * derived from the marker; `true` = we returned from a redirect and
 * `getRedirectResult` hasn't settled; `false` = settled (or never pending).
 *
 * Why a latch and not a live sessionStorage peek: `AuthGateProvider`'s
 * mount effect clears the marker at the app root, but LAZY route chunks
 * (board, quiz, results) mount after that effect has run — a live peek
 * there reads "no marker" and misses the still-unsettled sign-in. The
 * latch derives once from the marker (before it's cleared) and flips
 * false exactly when `consumeAuthRedirectResult` settles.
 */
let redirectPendingLatch: boolean | null = null;

/**
 * True when we're on the return leg of a mobile OAuth sign-in that
 * `getRedirectResult` hasn't finalized yet. During that window Firebase
 * restores the persisted ANON session first, so auth-branching UI (header
 * avatar, sign-in CTAs, board empty states) would render "signed out" for
 * the ~1-2s the finalize round-trip takes — reading as "the sign-in didn't
 * take." `useAuthInFlux` reads this at mount to hold neutral UI instead.
 *
 * Non-destructive on purpose: `consumeAuthRedirectResult` owns clearing
 * the marker AND settling the latch.
 */
export function hasPendingAuthRedirect(): boolean {
  if (redirectPendingLatch === null) {
    try {
      const marker = parseRedirectMarker(
        sessionStorage.getItem(REDIRECT_MARKER_KEY),
      );
      redirectPendingLatch =
        marker !== null && Date.now() - marker.ts < REDIRECT_PENDING_MAX_AGE_MS;
    } catch {
      redirectPendingLatch = false;
    }
  }
  return redirectPendingLatch;
}

/** Test-only: clear the module latch so each test derives fresh state. */
export function __resetRedirectPendingLatchForTests(): void {
  redirectPendingLatch = null;
}

/**
 * LIVE (non-latched) marker peek — unlike `hasPendingAuthRedirect`, never
 * caches, so it is safe to call again later in the SAME page load after
 * `hasPendingAuthRedirect` has already latched its one-time answer (every
 * `AuthGateProvider` mount calls it at `false` long before any redirect is
 * initiated). Used right before a `pagehide` to ask "is a redirect WE
 * initiated in flight right now" — the marker is written synchronously,
 * immediately before `signInWithRedirect`/`linkWithRedirect` navigates away,
 * so by the time `pagehide` fires it is reliably present.
 */
export function isAuthRedirectInFlight(): boolean {
  try {
    const marker = parseRedirectMarker(sessionStorage.getItem(REDIRECT_MARKER_KEY));
    return marker !== null && Date.now() - marker.ts < REDIRECT_PENDING_MAX_AGE_MS;
  } catch {
    return false;
  }
}

/**
 * Mint a merge token while we're still authenticated as the anon uid, then
 * return both. Callers capture this BEFORE switching auth.
 */
async function captureMergeIntent(authInstance: Auth): Promise<{
  fromUid: string;
  token: string;
} | null> {
  const fromUid = authInstance.currentUser?.uid;
  if (!fromUid) return null;
  try {
    const result = await mintMergeToken({});
    return { fromUid, token: result.data.token };
  } catch (err) {
    console.error('[accountAuth] mintMergeToken failed:', err);
    return null;
  }
}

/**
 * Best-effort merge from anon uid into the now-signed-in permanent uid.
 * Idempotent server-side via `_mergedFrom` markers — safe to retry.
 *
 * The caller is expected to have flipped MergeStatus to 'merging' BEFORE
 * the auth-state change (so listeners pause from the moment the uid
 * swaps). This wrapper transitions to 'merged' on success or 'failed' on
 * mergeGiftFlow error.
 */
async function tryMerge(
  intent: { fromUid: string; token: string } | null,
  onMergeStatus?: OnMergeStatus,
): Promise<void> {
  if (!intent) return;
  try {
    await mergeGiftFlow(intent);
    onMergeStatus?.('merged');
  } catch (err) {
    console.error('[accountAuth] mergeGiftFlow failed:', err);
    onMergeStatus?.('failed');
  }
}

/**
 * The OTP sign-in + merge path: sign in with a verified-OTP custom token,
 * merging the anonymous board into the signed-in account whenever the identity
 * actually changes. Uses `signInWithCustomToken` (vs. `signInWithCredential`)
 * plus `captureMergeIntent` + `tryMerge`. Since v4's OAuth-redirect return no
 * longer merges (guest board left behind on sign-in to an existing account —
 * feedback_no_merge_into_existing_account), THIS is the only path in the module
 * that merges; it is the current-production OTP flow and is out of scope for the
 * v4 no-merge rule.
 *
 *   1. While still anon A, mint a merge token (`captureMergeIntent`). Only for
 *      an ANONYMOUS caller — a permanent user must never be the `fromUid` of a
 *      merge (that would cascade-delete a real account).
 *   2. Flip 'merging' BEFORE the auth swap (sheet bug #58) so
 *      `useRecommendationDoc` holds prior state from the instant the uid
 *      changes, instead of flashing "couldn't find this recommendation" while
 *      the signed-in account's subtree is still empty.
 *   3. `signInWithCustomToken` → identity becomes X.
 *   4. X !== A (logged into an existing account, or a fresh one) → `mergeGiftFlow`
 *      copies A's subtree into X (idempotent; cascade-deletes A only on
 *      success, so a failed merge orphans rather than loses).
 *      X === A (anon upgraded in place — same uid) → no merge; release the gate
 *      and let the unused 60s token expire.
 *
 * The FE decides purely by comparing the uid before/after sign-in — no BE
 * "mergeCandidate" flag needed. Best-effort throughout: a merge failure is
 * logged, flips 'failed', and does NOT reject (sign-in already succeeded).
 */
export async function signInWithCustomTokenAndMerge(
  authInstance: Auth,
  customToken: string,
  onMergeStatus?: OnMergeStatus,
): Promise<void> {
  // Capture intent while still anon (the custom token hasn't been redeemed
  // yet, so currentUser is still the anon uid A). Non-anon callers never merge.
  const intent = authInstance.currentUser?.isAnonymous
    ? await captureMergeIntent(authInstance)
    : null;
  // Flip 'merging' BEFORE the auth-state change (sheet bug #58). Held whenever
  // an intent exists, released below the moment we know the uid didn't actually
  // change.
  if (intent) onMergeStatus?.('merging');
  try {
    await signInWithCustomToken(authInstance, customToken);
  } catch (err) {
    if (intent) onMergeStatus?.('idle');
    throw err;
  }
  const afterUid = authInstance.currentUser?.uid ?? null;
  if (intent && afterUid !== intent.fromUid) {
    // Identity changed → merge the anon board into the signed-in account.
    await tryMerge(intent, onMergeStatus);
  } else if (intent) {
    // Upgrade-in-place (same uid): nothing to merge, release the gate.
    onMergeStatus?.('idle');
  }
}

/**
 * Recover an OAuthCredential from a `credential-already-in-use` error. Tries
 * `OAuthProvider.credentialFromError` (works for Apple and any OAuth provider)
 * first, then `GoogleAuthProvider.credentialFromError` as a typed fallback.
 * Either yields a credential we can hand to `signInWithCredential`.
 */
function credentialFromError(err: unknown): AuthCredential | null {
  type CredFromErrInput =
    Parameters<typeof OAuthProvider.credentialFromError>[0];
  return (
    OAuthProvider.credentialFromError(err as CredFromErrInput) ??
    GoogleAuthProvider.credentialFromError(err as CredFromErrInput)
  );
}

/**
 * Mobile-redirect counterpart: when an OAuth provider returns from
 * `linkWithRedirect` with `auth/credential-already-in-use`, recover the
 * credential and sign in as the EXISTING user instead of stranding the user
 * unsigned — WITHOUT merging (the guest board is left behind, per the v4 One
 * Rule feedback_no_merge_into_existing_account). Provider-agnostic — handles
 * both Google and Apple via `credentialFromError` (which tries both extractors).
 *
 * Returns `markerPresent` so the caller can distinguish "cold page load,
 * nothing to consume" from "we kicked off a redirect but no credential came
 * back" — only the latter is a Bug 2 signal worth surfacing to the user.
 * `errorCode` is populated when getRedirectResult itself failed in a way we
 * suppressed (e.g. unrecoverable `credential-already-in-use`), so telemetry
 * can pivot on the failure mode.
 */
export interface ConsumeRedirectResult {
  user: User | null;
  markerPresent: boolean;
  /** True when this redirect created a brand-new provider account (vs. signing
   *  into an existing one). Derived from `getAdditionalUserInfo`; only
   *  meaningful when `user` is present, `false` otherwise. The claim flow
   *  branches on it: new → claim the board, existing → "already have an account".
   *
   *  ⚠ Trustworthy ONLY on the `signin` leg. A successful `link` reports false
   *  for a brand-new account — pair it with `operation` (THE-712). */
  isNewUser: boolean;
  /** The call we initiated, read back off the redirect marker. `null` when no
   *  marker came back, or when it predates this field. */
  operation: RedirectOperation | null;
  /** True only on the `auth/credential-already-in-use` recovery below: the
   *  provider identity already belonged to a real account and we signed into
   *  THAT one. The one unambiguous "existing account" signal on the link leg. */
  recoveredExistingAccount: boolean;
  errorCode?: string;
}

export async function consumeAuthRedirectResult(
  authInstance: Auth = defaultAuth,
): Promise<ConsumeRedirectResult> {
  // Derive the pending latch from the marker BEFORE clearing it, so
  // late-mounting consumers (lazy route chunks) can still read "a sign-in
  // is mid-flight" after the sessionStorage marker is gone.
  hasPendingAuthRedirect();
  const marker = readAndClearRedirectMarker();
  const markerPresent = marker !== null;
  try {
    const result = await getRedirectResult(authInstance);
    const isNewUser = result ? getAdditionalUserInfo(result)?.isNewUser ?? false : false;
    return {
      user: result?.user ?? null,
      markerPresent,
      isNewUser,
      operation: operationFromResult(result) ?? marker?.operation ?? null,
      recoveredExistingAccount: false,
    };
  } catch (err) {
    const code = (err as { code?: string })?.code;
    if (code === 'auth/credential-already-in-use') {
      const credential = credentialFromError(err);
      if (credential) {
        // The guest linked into an OAuth account that ALREADY belongs to a real
        // user. Sign into THAT account only — NO merge: the guest board is left
        // behind (v4 One Rule, feedback_no_merge_into_existing_account). This
        // mirrors the v4 desktop popup path (v4Auth.signInWithOAuthProvider),
        // the sibling that INITIATES this redirect. Boards attach ONLY at
        // brand-new-account creation, which is the success return above
        // (`linkWithRedirect` kept the anon uid).
        const result = await signInWithCredential(authInstance, credential);
        return {
          user: result.user,
          markerPresent,
          isNewUser: false,
          // No fresh result to read `operationType` off (the original
          // `getRedirectResult` call is what rejected) — `recoveredExistingAccount`
          // below is the proven signal for this leg regardless of `operation`,
          // so the marker is enough here.
          operation: marker?.operation ?? null,
          // The ONE place on the link leg where "existing account" is proven:
          // Firebase rejected the link because the identity was already taken.
          recoveredExistingAccount: true,
        };
      }
      return {
        user: null,
        markerPresent,
        isNewUser: false,
        operation: marker?.operation ?? null,
        recoveredExistingAccount: false,
        errorCode: code,
      };
    }
    throw err;
  } finally {
    // Settled — success, recovery, or failure. Pending UI must release on
    // every path; the grace windows in `useAuthInFlux` bound the interim.
    redirectPendingLatch = false;
  }
}

export async function signOutUser(
  authInstance: Auth = defaultAuth,
): Promise<void> {
  await signOut(authInstance);
  // Clear the optimistic has-boards hint (hasBoardsHint.ts) — it belongs
  // to the account that just left. Without this, the layout gate and the
  // desktop nav links would keep rendering the signed-in state off the
  // stale hint until the anon re-mint hydrates an empty friends list.
  writeHasBoardsHint(false);
}
